"use client";

import React, {
  createContext,
  type Dispatch,
  type SetStateAction,
  useEffect,
} from "react";
import { useParams } from "next/navigation";
import { useDispatch } from "react-redux";
import { store, type AppDispatch } from "../../src/store/index";
import { useEvents } from "./pusher";
import type { ChannelEventHandler, ChannelSubscription } from "./pusher";
import { close as closeSideNavigation } from "../../src/store/features/layout/sideNavigationSlice";
import {
  receiveMessage,
  type ListMessage,
} from "../../src/store/features/chat/chatSlice";
import {
  applyMessageStatusUpdate,
  getContact,
  markContactRead,
} from "../../src/store/features/chat/chatSlice";
import type { GetContactOutputType } from "skedyul-core/features/conversations";
import { type RouterOutputs } from "../../src/utils/api";
import { useSessionStorage } from "@uidotdev/usehooks";
import { useBreakpoint } from "skedyul-ui/src/hooks/useBreakpoint";
import { useRouter } from "next/navigation";
// workplace unread count for global messages is handled in UserProvider
// import { TeamMember } from "@store/features/account/workplaceSlice";
// import {
//   AppCommunicationChannel,
//   CommunicationChannel,
//   CommunicationChannelSubscription,
//   Contact,
// } from "skedyul-db";

// removed markContactRead and getContact responsibilities from provider; handled via hook

// Define context
export interface ChatWindowContextType {
  view: "open" | "collapsed" | "hidden";
  contactId: string;
  handleChange: Dispatch<
    SetStateAction<{
      view: "open" | "collapsed" | "hidden";
      contactId: string;
    }>
  >;
}

// Props interface
interface ChatWindowProviderProps {
  children: React.ReactNode;
}

let chatWindowState: {
  view: "open" | "collapsed" | "hidden";
  contactId: string;
} = {
  view: "hidden",
  contactId: "",
};

let handleWindowChange: ChatWindowContextType["handleChange"];

export const handleWindowChangeDispatch = (props: {
  view: "open" | "collapsed" | "hidden";
  contactId: string;
}) => {
  chatWindowState = props;
  handleWindowChange(props);
};

export interface ChatWindowProps {
  view: "open" | "collapsed" | "hidden";
  contactId: string;
  handleChange: Dispatch<
    SetStateAction<{
      view: "open" | "collapsed" | "hidden";
      contactId: string;
    }>
  >;
}

export const getChatWindowState = () => chatWindowState;

const ChatWindowContext = createContext<ChatWindowContextType>({
  view: "hidden",
  contactId: "",

  handleChange: () => {},
});

export const useChatWindow = () => React.useContext(ChatWindowContext);

const ChatWindowProvider: React.FC<ChatWindowProviderProps> = ({
  children,
}) => {
  const [chatWindow, handleChange] = useSessionStorage("chat-window", {
    view: "hidden" as "open" | "collapsed" | "hidden",
    contactId: "",
  });

  const { isAboveMd } = useBreakpoint("md");
  const router = useRouter();

  const { view, contactId } = chatWindow;

  const params = useParams<{ subdomain: string }>();
  const dispatch = useDispatch<AppDispatch>();
  // useEvents will manage pusher subscriptions centrally

  handleWindowChange = handleChange;
  chatWindowState = chatWindow;

  // Mobile redirect when chat window is opened on small screens
  useEffect(() => {
    if (!isAboveMd && contactId && view === "open") {
      router.push(`/${params.subdomain}/conversations/${contactId}`);
      store.dispatch(closeSideNavigation({ position: "right" }));
    }
  }, [isAboveMd, view, contactId, params.subdomain, router]);

  // Workplace-scope message.created is now handled centrally in UserProvider

  // Fetch contact data and mark as read when chat window is opened/collapsed
  useEffect(() => {
    if (!contactId || view === "hidden") return;
    void dispatch(
      getContact({
        contactId,
        chatId: undefined,
        cursor: undefined,
        concat: false,
      }),
    );
    const timer = setTimeout(() => {
      void store.dispatch(markContactRead({ contactId }));
    }, 1500);
    return () => clearTimeout(timer);
  }, [contactId, view, dispatch]);

  // Subscribe to the conversation instance channel for the open contact
  useEvents(
    React.useMemo(() => {
      if (!contactId) return null;
      const onCreated = (incoming: unknown) => {
        try {
          const env =
            typeof incoming === "object" && incoming !== null
              ? (incoming as Record<string, unknown>)
              : undefined;
          const payload = (env && env["payload"]) || incoming;
          const contactPayload = payload as RouterOutputs["chat"]["getContact"];
          const last = (contactPayload?.messages || [])[0];
          if (!last) return;
          void dispatch(
            receiveMessage({
              contact: contactPayload as unknown as GetContactOutputType,
              message: last as unknown as ListMessage,
              verified: true,
              member: undefined,
            }),
          );
        } catch {
          // ignore parse errors
        }
      };

      const onCreatedHandler: ChannelEventHandler = (data) => {
        try {
          const env =
            typeof data === "object" && data !== null
              ? (data as Record<string, unknown>)
              : undefined;
          const payload = (env && env["payload"]) || data;
          onCreated(payload as RouterOutputs["chat"]["getContact"]);
        } catch {
          // ignore parse errors
        }
      };

      const statusHandler: ChannelEventHandler = (data) => {
        try {
          const env =
            typeof data === "object" && data !== null
              ? (data as Record<string, unknown>)
              : undefined;
          const payload = ((env && env["payload"]) || data) as {
            messageId: string;
            status:
              | "in_flight"
              | "sent"
              | "received"
              | "rejected"
              | "delivered";
          };
          void dispatch(applyMessageStatusUpdate(payload));
        } catch {
          // ignore parse errors
        }
      };

      const subscriptions: ChannelSubscription[] = [
        {
          channel: ({ subdomain }) =>
            subdomain ? `private-${subdomain}-contact-${contactId}` : "",
          events: {
            "message.created": onCreatedHandler,
            "message.sent": statusHandler,
            "message.rejected": statusHandler,
            "message.in_flight": statusHandler,
            "message.delivered": statusHandler,
          },
        },
      ];

      return subscriptions;
    }, [dispatch, contactId]),
  );

  return (
    <ChatWindowContext.Provider
      value={{ view: view, contactId: contactId, handleChange }}
    >
      {children}
    </ChatWindowContext.Provider>
  );
};

export default ChatWindowProvider;
